<template>
  <mi-card
    :id="`activity-center-notification-${ activity.id }`"
    class="activity-center-activity-card"
    :class="`activity-center-activity-card--${ activity.status }`"
  >
    <div class="activity-center-activity-card__top">
      <span
        class="activity-center-activity-card__status-icon"
        :class="`activity-center-activity-card__status-icon--${ activity.status }`"
      >
        <template v-if="isFailed">
          <mi-icon size="10px" name="close"></mi-icon>
        </template>
        <template v-if="isCompleted">
          <mi-icon size="10px" name="check"></mi-icon>
        </template>

        <span
          v-if="isInProgress"
          class="activity-center-activity-card__spinner"
          aria-hidden="true"
        >
          <span
            v-for="barIndex in 12"
            :key="barIndex"
            class="activity-center-activity-card__spinner-bar"
          ></span>
        </span>
      </span>

      <div class="activity-center-activity-card__title-wrapper">
        <span class="activity-center-activity-card__title">
          {{ activity.title }}
        </span>
        <span
          v-if="isNewBadgeVisible"
          class="activity-center-activity-card__new-badge"
        >
          NEW
        </span>
        <span class="activity-center-activity-card__time">
          {{ createdAtLabel }}
        </span>
      </div>

      <div class="activity-center-activity-card__actions">
        <mi-btn
          v-if="isRetryable"
          class="activity-center-activity-card__retry-btn"
          :loading="isRetrying"
          :disable="isRetrying"
          unelevated
          @click="retry"
        >
          Retry
        </mi-btn>

        <mi-btn
          v-if="isCompleted && goDestination"
          class="activity-center-activity-card__action-btn"
          @click="goToDestination"
        >
          Go
        </mi-btn>

        <button
          class="activity-center-activity-card__details-btn"
          :aria-expanded="isDetailsExpanded"
          @click="toggleDetails"
        >
          {{ isDetailsExpanded ? 'Hide details' : 'Show details' }}
        </button>
      </div>
    </div>

    <template v-if="isDetailsExpanded">
      <div
        v-if="isInProgress"
        class="activity-center-activity-card__progress"
      >
        <div class="activity-center-activity-card__progress-header">
          <span>Estimated progress</span>
          <span>{{ activity.progress }}%</span>
        </div>

        <div class="activity-center-activity-card__progress-track">
          <div
            class="activity-center-activity-card__progress-value"
            :style="{ width: `${ activity.progress }%` }"
          ></div>
        </div>
      </div>

      <div class="activity-center-activity-card__message">
        {{ activity.message }}
      </div>
    </template>
  </mi-card>
</template>

<script>
  import { computed, ref } from 'vue'
  import { useRouter } from 'vue-router'
  import {
    ACTIVITY_CENTER_TAB_NAMES,
    getActivityCenterDestination,
    getActivityCenterRelativeTime
  } from '@/constants/activityCenter'
  import { retryJob } from '@/api/rest/activity-center'
  import notify from '@/utils/notify'
  import { NEGATIVE } from '@/plugins/quasar/notifications/notificationTypes'
  import MiCard from '@/packages/@mi-library/MiCard/MiCard.vue'
  import MiBtn from '@/packages/@mi-library/MiBtn/MiBtn.vue'

  export default {
    name: 'ActivityCenterActivityCard',
    components: { MiCard, MiBtn },
    props: {
      activity: {
        type: Object,
        required: true
      }
    },
    emits: ['navigate', 'retry', 'retry:error'],
    setup(props, { emit }) {
      const router = useRouter()
      const isDetailsExpanded = ref(true)
      const isRetrying = ref(false)

      const isFailed = computed(() => props.activity.status === ACTIVITY_CENTER_TAB_NAMES.FAILED)
      const isInProgress = computed(() => props.activity.status === ACTIVITY_CENTER_TAB_NAMES.IN_PROGRESS)
      const isCompleted = computed(() => props.activity.status === ACTIVITY_CENTER_TAB_NAMES.COMPLETED)
      const createdAtLabel = computed(() => getActivityCenterRelativeTime(props.activity.createdAt))

      const isRetryable = computed(() => isFailed.value && props.activity.canRetry === true)

      const isLessThan24HoursOld = computed(() => {
        if (!props.activity.createdAt) return false

        const createdAt = new Date(props.activity.createdAt)

        if (Number.isNaN(createdAt.getTime())) return false

        const differenceInMilliseconds = Date.now() - createdAt.getTime()

        return differenceInMilliseconds >= 0 && differenceInMilliseconds <= 24 * 60 * 60 * 1000
      })

      const isNewBadgeVisible = computed(() => isCompleted.value
        && isLessThan24HoursOld.value)

      const toggleDetails = () => {
        isDetailsExpanded.value = !isDetailsExpanded.value
      }

      const goDestination = computed(() => getActivityCenterDestination(props.activity))

      const goToDestination = () => {
        if (!goDestination.value) return

        router.push(goDestination.value)
        emit('navigate')
      }

      const retry = async () => {
        if (isRetrying.value || !isRetryable.value) return

        const { serviceName, correlationId } = props.activity

        if (!serviceName || !correlationId) return

        isRetrying.value = true

        try {
          await retryJob({ serviceName, correlationId })

          emit('retry')
        }
        catch (error) {
          notify({
            title: 'Retry failed',
            content: 'The operation could not be retried. Please try again.',
            type: NEGATIVE
          })

          emit('retry:error')
        }
        finally {
          isRetrying.value = false
        }
      }

      return {
        isDetailsExpanded,
        isFailed,
        isInProgress,
        isRetryable,
        toggleDetails,
        isCompleted,
        createdAtLabel,
        isNewBadgeVisible,
        goDestination,
        goToDestination,
        isRetrying,
        retry
      }
    }
  }
</script>

<style lang="scss" scoped>
  .activity-center-activity-card {
    padding: 18px 20px;
    border: 1px solid $mi-silver-300;
    border-left-width: 4px;
    border-radius: 4px;
    background-color: $mi-white;

    &--failed {
      border-color: #cc0000;
    }

    &--in-progress {
      border-color: #4b96d2;
    }

    &--completed {
      border-color: #b3cd63;
    }

    &__top {
      display: flex;
      align-items: center;
      gap: 16px;
    }

    &__status-icon {
      width: 22px;
      height: 22px;
      flex-shrink: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-family: Arial, sans-serif;
      font-weight: 700;
      font-size: 18px;
      line-height: 18px;

      &--failed {
        border-radius: 50%;
        background-color: #cc0000;
        color: $mi-white;
      }

      &--in-progress {
        color: #4b96d2;
      }

      &--completed {
        border-radius: 50%;
        background-color: #b3cd63;
        color: $mi-white;
      }
    }

    &__spinner {
      position: relative;
      width: 26px;
      height: 26px;
      display: inline-block;
    }

    &__spinner-bar {
      position: absolute;
      top: 1px;
      left: 12px;
      width: 2px;
      height: 7px;
      border-radius: 2px;
      background-color: #459eeb;
      transform-origin: 1px 12px;
      animation: activity-center-spinner-fade 1.2s linear infinite;
    }

    @for $i from 1 through 12 {
      &__spinner-bar:nth-child(#{$i}) {
        transform: rotate(#{($i - 1) * 30}deg);
        animation-delay: #{($i - 12) * .1}s;
      }
    }

    @keyframes activity-center-spinner-fade {
      0% {
        opacity: 1;
      }

      100% {
        opacity: .2;
      }
    }

    &__title-wrapper {
      display: flex;
      align-items: center;
      gap: 12px;
      flex: 1;
      min-width: 0;
    }

    &__title {
      font-family: $mi-typography-font-family-condensed;
      font-weight: 700;
      font-size: 16px;
      line-height: 24px;
      color: $mi-anthracite-800;
    }

    &__time {
      font-family: $mi-typography-font-family-condensed;
      font-size: 14px;
      line-height: 21px;
      color: $mi-anthracite-400;
    }

    &__actions {
      display: flex;
      align-items: center;
      gap: 16px;
      margin-left: auto;
    }

    &__retry-btn {
      padding: 10px 18px;
      border: 0;
      background-color: $mi-anthracite-900;
      cursor: pointer;
      font-family: $mi-typography-font-family-condensed;
      font-weight: 700;
      font-size: 14px;
      line-height: 18px;
      color: $mi-white;
    }

    &__action-btn {
      padding: 10px 22px;
      border: 0;
      background-color: $mi-anthracite-900;
      cursor: pointer;
      font-family: $mi-typography-font-family-condensed;
      font-weight: 700;
      font-size: 14px;
      line-height: 18px;
      color: $mi-white;
    }

    &__details-btn {
      padding: 0;
      border: 0;
      background: transparent;
      cursor: pointer;
      font-family: $mi-typography-font-family;
      font-size: 16px;
      line-height: 24px;
      color: $mi-anthracite-800;
      text-decoration: underline;
    }

    &__progress {
      margin-top: 18px;
      padding-left: 2px;
    }

    &__progress-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 8px;
      font-family: $mi-typography-font-family-condensed;
      font-weight: 700;
      font-size: 14px;
      line-height: 20px;
      color: $mi-anthracite-800;
    }

    &__progress-track {
      height: 8px;
      background-color: $mi-silver-100;
    }

    &__progress-value {
      height: 100%;
      background-color: #244a82;
    }

    &__message {
      position: relative;
      margin-top: 18px;
      padding-left: 14px;
      font-family: $mi-typography-font-family-condensed;
      font-size: 14px;
      line-height: 21px;
      color: #cc0000;

      &:before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        width: 2px;
        height: 100%;
        background-color: currentColor;
      }
    }

    &--in-progress &__message {
      color: #244a82;
    }

    &--completed &__message {
      color: #2d6300;
    }

    &__new-badge {
      padding: 2px 7px;
      border-radius: 12px;
      background-color: #dfe9bc;
      font-family: $mi-typography-font-family-condensed;
      font-weight: 400;
      font-size: 12px;
      line-height: 14px;
      color: #2d6300;
    }
  }
</style>
